import type { PaymentIntentResult } from "@stripe/stripe-js";
import { useMutation } from "react-query";
import {
  PaymentMethodType,
  isAffirm,
  isAfterpayClearpay,
  isKlarna,
} from "@/react/helpers/paywallCheckoutHelpers";
import type { CommunityMemberBillingInfo } from "@/react/types";
import { paywallCheckoutApi } from "@circle-react/api/paywallCheckoutApi";
import type {
  PixPrepareResponseEndpoint,
  PrepareRequestEndpoint,
} from "@circle-react/api/paywallCheckoutApi.types";
import { usePaywallCheckoutContext } from "@circle-react/contexts/Paywalls/paywallCheckoutContext";
import { CheckoutError } from "../../CheckoutError";
import { constructReturnUrl } from "../../helpers";
import { getCurrentUserName } from "../../helpers/users";
import { useGetStripeElements } from "../../useStripeElements";

interface ConfirmPaymentParams {
  billing_info_attributes: Omit<CommunityMemberBillingInfo, "id">;
  email: string;
  name: string;
  payment_intent_client_secret: string;
}

export const useBuyNowPayLater = () => {
  const {
    currentCommunity,
    stripePaymentMethodType,
    currentUser,
    memberBillingInfo,
  } = usePaywallCheckoutContext();
  const getStripeElements = useGetStripeElements();

  const getBillingField = (
    field:
      | "address_country"
      | "address_line1"
      | "address_city"
      | "address_postal_code"
      | "address_state",
    params: ConfirmPaymentParams,
  ) =>
    memberBillingInfo?.[field] || params.billing_info_attributes[field] || "";

  const prepare = async (formData: any) => {
    const { stripe } = getStripeElements("useBuyNowPayLater.prepare");

    const {
      name,
      email,
      payment_method_type,
      paywall_price_id,
      coupon_code,
      community_member_billing_info_attributes,
      password,
    } = formData;

    let prepareResponse: PixPrepareResponseEndpoint;
    try {
      const prepareRequestParams: PrepareRequestEndpoint = {
        name,
        email,
        payment_method_type,
        paywall_price_id,
        community_id: currentCommunity.id,
        coupon_code,
        community_member_billing_info_attributes,
        password,
      };
      prepareResponse = await paywallCheckoutApi.prepare(prepareRequestParams);
    } catch (error: unknown) {
      throw CheckoutError.fromUnknownError("useBuyNowPayLater.prepare", error);
    }

    const confirmParams = {
      payment_intent_client_secret:
        prepareResponse.payment_intent_client_secret,
      billing_info_attributes: community_member_billing_info_attributes,
      email: email || currentUser?.email,
      name: name || getCurrentUserName(currentUser),
    };

    let paymentIntentResult: PaymentIntentResult | undefined;
    try {
      if (isKlarna(formData.payment_method_type)) {
        paymentIntentResult = await stripe.confirmKlarnaPayment(
          confirmParams.payment_intent_client_secret,
          {
            payment_method: {
              billing_details: {
                email: confirmParams.email,
                address: {
                  country: getBillingField("address_country", confirmParams),
                },
              },
            },
            return_url: constructReturnUrl(PaymentMethodType.KLARNA),
          },
        );
      } else if (isAfterpayClearpay(formData.payment_method_type)) {
        paymentIntentResult = await stripe.confirmAfterpayClearpayPayment(
          confirmParams.payment_intent_client_secret,
          {
            payment_method: {
              billing_details: {
                email: confirmParams.email,
                name: confirmParams.name,
                address: {
                  country: getBillingField("address_country", confirmParams),
                  line1: getBillingField("address_line1", confirmParams),
                  city: getBillingField("address_city", confirmParams),
                  postal_code: getBillingField(
                    "address_postal_code",
                    confirmParams,
                  ),
                },
              },
            },
            shipping: {
              name: confirmParams.name,
              address: {
                country: getBillingField("address_country", confirmParams),
                line1: getBillingField("address_line1", confirmParams),
                city: getBillingField("address_city", confirmParams),
                postal_code: getBillingField(
                  "address_postal_code",
                  confirmParams,
                ),
              },
            },
            return_url: constructReturnUrl(PaymentMethodType.AFTERPAY_CLEARPAY),
          },
        );
      } else if (isAffirm(formData.payment_method_type)) {
        paymentIntentResult = await stripe.confirmAffirmPayment(
          confirmParams.payment_intent_client_secret,
          {
            payment_method: {
              billing_details: {
                email: confirmParams.email,
                name: confirmParams.name,
                address: {
                  country: getBillingField("address_country", confirmParams),
                  line1: getBillingField("address_line1", confirmParams),
                  city: getBillingField("address_city", confirmParams),
                  state: getBillingField("address_state", confirmParams),
                  postal_code: getBillingField(
                    "address_postal_code",
                    confirmParams,
                  ),
                },
              },
            },
            shipping: {
              name: confirmParams.name,
              address: {
                country: getBillingField("address_country", confirmParams),
                line1: getBillingField("address_line1", confirmParams),
                city: getBillingField("address_city", confirmParams),
                state: getBillingField("address_state", confirmParams),
                postal_code: getBillingField(
                  "address_postal_code",
                  confirmParams,
                ),
              },
            },
            return_url: constructReturnUrl(PaymentMethodType.AFFIRM),
          },
        );
      }
    } catch (error: unknown) {
      throw CheckoutError.fromUnknownError(
        "useBuyNowPayLater.prepare.confirmPayment",
        error,
      );
    }

    // We should never get below this point, each "confirmPayment" method should redirect the user to the
    // payment method's confirmation page
    if (!paymentIntentResult) {
      throw CheckoutError.generic("useBuyNowPayLater.prepare.confirmPayment");
    }
    if (paymentIntentResult.error) {
      throw CheckoutError.fromStripeError(
        "useBuyNowPayLater.prepare.confirmPayment",
        paymentIntentResult.error,
      );
    }

    return prepareResponse;
  };

  const onSubmitMutation = useMutation<any, any, any>(async formData => {
    formData.payment_method_type = stripePaymentMethodType;
    await prepare(formData);
  });

  return {
    onSubmitMutation,
  };
};
